import styled, { keyframes } from 'styled-components';

/* ---------------- Animations ---------------- */

const orbit = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

const dots = keyframes`
  0% { content: ''; }
  33% { content: '.'; }
  66% { content: '..'; }
  100% { content: '...'; }
`;

/* ---------------- Container ---------------- */

export const LoaderContainer = styled.div<{
  fullHeight?: boolean;
  isDarkTheme?: boolean;
}>`
  position: relative;
  width: 100%;
  min-height: ${({ fullHeight }) => (fullHeight ? '100%' : '240px')};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  overflow: hidden;

  background: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(14, 14, 18, 0.75)' : 'rgba(255, 255, 255, 0.75)'};

  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);

  box-shadow: ${({ isDarkTheme }) =>
    isDarkTheme
      ? '0 40px 80px rgba(0,0,0,0.6)'
      : '0 40px 80px rgba(0,0,0,0.12)'};
`;

/* ---------------- Background Glow ---------------- */

export const GradientBackground = styled.div<{ isDarkTheme?: boolean }>`
  position: absolute;
  inset: -40%;

  background: linear-gradient(120deg, #0a51c5, #4caf50, #ff9800, #9c27b0);

  background-size: 300% 300%;
  animation: ${shimmer} 16s linear infinite;
  opacity: ${({ isDarkTheme }) => (isDarkTheme ? 0.22 : 0.12)};
  filter: blur(80px);
`;

/* ---------------- Spinner Wrapper ---------------- */

export const SpinnerContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

/* ---------------- Calm Orbit Spinner ---------------- */

export const Spinner = styled.div<{
  isDarkTheme?: boolean;
  size?: 'small' | 'large' | 'medium';
}>`
  position: relative;
  width: ${({ size }) =>
    size === 'small' ? '48px' : size === 'large' ? '96px' : '72px'};
  height: ${({ size }) =>
    size === 'small' ? '48px' : size === 'large' ? '96px' : '72px'};

  border-radius: 50%;
  animation: ${orbit} 3.2s linear infinite;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid
      ${({ isDarkTheme }) =>
        isDarkTheme ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)'};
  }

  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ isDarkTheme }) => (isDarkTheme ? '#8ab4ff' : '#0a51c5')};
    box-shadow: 0 0 14px
      ${({ isDarkTheme }) =>
        isDarkTheme ? 'rgba(138,180,255,0.75)' : 'rgba(10,81,197,0.45)'};
  }
`;

/* ---------------- Text ---------------- */

export const LoadingText = styled.span<{ isDarkTheme?: boolean }>`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;

  color: ${({ isDarkTheme }) => (isDarkTheme ? '#e0e6ff' : '#444')};

  animation: ${pulse} 2.6s ease-in-out infinite;

  &::after {
    content: '';
    animation: ${dots} 1.8s steps(3, end) infinite;
  }
`;
